// IMPORTS
// Pinia
import { defineStore } from 'pinia';

/**
 * Authenticate State
 *  data: Data for the logged in user
 */
interface AuthenticateState {
  userId?: string;
  email?: string;
  userName?: string;
  firstName?: string;
  lastName?: string;
  // Role can be learner, teacher etc.
  role?: string;
  /**
   * isLtiSession is true if LaunchType is LTI(CambridgeOne is launched from LTI platforms)
   * For normal launches, isLtiSession is false
   ***/
  isLtiSession?: boolean;
  /***
   * User Login provider. This data is fetched from SAP-CDC
   * It represents the identities of the user
   * - site - If the user has registered using email and password
   * - google - If the user registered using google
   * - facebook - If the user has registered using facebook
   *
   * Note, the user can have multiple loginProviders, in case the user
   * has linked the multiple identites
   ***/
  loginProvider?: string;
  /**
   * ParentId is the userId of the parent user
   * This is used to identify the parent user in case of child user
   * For other users, parentId will not be present
   */
  parentUUID?: string;
  /**
   * User's country
   */
  country?: string;
  /**
   * This is used to identify if the user is a part of any school or new user
   */
  isNewUser?: boolean;

  /**
   * This is used to identify if the user is a part of any MQA school
   */
  isMQA?: boolean;

  /**
   * This is used to identify integration name
   */
  integrationName?: string;
}

/**
 * Authenticate Store
 */
export const useAuthenticateStore = defineStore('Authenticate', {
  // Return the initial state of the store
  state: (): AuthenticateState => ({}),

  // Store Getters
  getters: {},

  // Store Actions
  actions: {
    /**
     * @param data - email, userId, firstName, lastName, role, isLtiSession, integrationName
     * This is used to load the user data for SSR rendering
     */
    load(data: {
      email?: string;
      userId?: string;
      firstName?: string;
      lastName?: string;
      role?: string;
      isLtiSession?: boolean;
      integrationName?: string;
      country?: string;
    }) {
      const fieldMap: Record<string, keyof typeof data> = {
        email: 'email',
        userId: 'userId',
        firstName: 'firstName',
        lastName: 'lastName',
        role: 'role',
        isLtiSession: 'isLtiSession',
        integrationName: 'integrationName',
        country: 'country'
      };

      Object.entries(fieldMap).forEach(([prop, key]) => {
        if (data[key] !== undefined) {
          (this as any)[prop] = data[key];
        }
      });
    },
    /**
     * @param data - userName, loginProvider, parentUUID, country
     * This is used to load the user data from SAP-CDC
     * SAP-CDC data not available on backend (SSR). Only loaded on frontend
     */
    loadFromSAPCDC(data: {
      userName?: string;
      loginProvider?: string;
      parentUUID?: string;
      country?: string;
    }) {
      this.userName = data.userName;
      this.loginProvider = data.loginProvider;
      this.parentUUID = data.parentUUID;
      this.country = data.country;
    },
    /**
     * @param data - isNewUser and isMQA
     * This is used to load the user data based on school data
     */
    loadSchoolBasedData(data: { isNewUser?: boolean; isMQA?: boolean }) {
      this.isNewUser = data.isNewUser;
      this.isMQA = data.isMQA;
    },
    /**
     * Reset the authenticate store to their initial state
     *
     * This is useful when the underlying data
     * is no longer valid or needs to be refreshed.
     */
    reset: function () {
      this.$reset();
    }
  }
});
